/**
 * Notification Management Page
 * Trang quản lý thông báo cho sinh viên và giảng viên
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Tooltip,
  Popconfirm,
  Switch,
  DatePicker
} from 'antd';
import {
  NotificationOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  EyeOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  UserOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import NotificationService, { Notification, NotificationStats } from '../../../services/notificationService';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const NotificationManagementPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [filters, setFilters] = useState<{ type?: string; targetPortal?: string; isPublished?: string }>({});
  
  // Modal states
  const [modalVisible, setModalVisible] = useState(false);
  const [editingNotification, setEditingNotification] = useState<Notification | null>(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadNotifications();
    loadStats();
  }, [pagination.page, pagination.limit, filters]);

  async function loadNotifications() {
    setLoading(true);
    try {
      const response = await NotificationService.getAdminNotifications({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });
      if (response.success) {
        setNotifications(response.data);
        setPagination(prev => ({ ...prev, total: response.pagination?.total || 0 }));
      }
    } catch (error: any) {
      message.error('Không thể tải danh sách thông báo');
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    try {
      const response = await NotificationService.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }

  function openCreateModal() {
    setEditingNotification(null);
    form.resetFields();
    form.setFieldsValue({
      type: 'general',
      priority: 0,
      targetPortal: 'all',
      isPublished: false
    });
    setModalVisible(true);
  }

  function openEditModal(notification: Notification) {
    setEditingNotification(notification);
    form.setFieldsValue({
      ...notification,
      expiresAt: notification.expiresAt ? dayjs(notification.expiresAt) : null
    });
    setModalVisible(true);
  }

  async function handleSubmit(values: any) {
    setSubmitting(true);
    try {
      const data = {
        ...values,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null
      };

      if (editingNotification) {
        await NotificationService.updateNotification(editingNotification.id, data);
        message.success('Cập nhật thông báo thành công');
      } else {
        await NotificationService.createNotification(data);
        message.success('Tạo thông báo thành công');
      }

      setModalVisible(false);
      form.resetFields();
      loadNotifications();
      loadStats();
    } catch (error: any) {
      message.error(error.message || 'Lỗi khi lưu thông báo');
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePublish(id: number, publish: boolean) {
    try {
      if (publish) {
        await NotificationService.publishNotification(id);
        message.success('Đã phát hành thông báo');
      } else {
        await NotificationService.unpublishNotification(id);
        message.success('Đã hủy phát hành thông báo');
      }
      loadNotifications();
      loadStats();
    } catch (error: any) {
      message.error(error.message || 'Lỗi khi cập nhật trạng thái');
    }
  }

  async function handleDelete(id: number) {
    try {
      await NotificationService.deleteNotification(id);
      message.success('Đã xóa thông báo');
      loadNotifications();
      loadStats();
    } catch (error: any) {
      message.error(error.message || 'Lỗi khi xóa thông báo');
    }
  }

  function getTypeTag(type: string) {
    const config: Record<string, { color: string; text: string }> = {
      general: { color: 'blue', text: 'Chung' },
      urgent: { color: 'red', text: 'Khẩn cấp' },
      academic: { color: 'purple', text: 'Học thuật' },
      financial: { color: 'gold', text: 'Tài chính' },
      event: { color: 'green', text: 'Sự kiện' }
    };
    const cfg = config[type] || config.general;
    return <Tag color={cfg.color}>{cfg.text}</Tag>;
  }

  function getPortalTag(portal: string) {
    if (portal === 'student') return <Tag icon={<UserOutlined />} color="cyan">Sinh viên</Tag>;
    if (portal === 'teacher') return <Tag icon={<TeamOutlined />} color="volcano">Giảng viên</Tag>;
    return <Tag color="default">Tất cả</Tag>;
  }

  const columns: ColumnsType<Notification> = [
    {
      title: 'Trạng thái',
      key: 'status',
      width: 100,
      render: (_, record) => (
        record.isPublished 
          ? <Tag color="green" icon={<CheckCircleOutlined />}>Đã phát hành</Tag>
          : <Tag color="default" icon={<ClockCircleOutlined />}>Nháp</Tag>
      )
    },
    {
      title: 'Đối tượng',
      dataIndex: 'targetPortal',
      key: 'targetPortal',
      width: 100,
      render: (portal) => getPortalTag(portal)
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => getTypeTag(type)
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title, record) => (
        <div>
          {record.priority > 0 && <Tag color="red" style={{ marginRight: 4 }}>Quan trọng</Tag>}
          <Text strong>{title}</Text>
        </div>
      )
    },
    {
      title: 'Tóm tắt',
      dataIndex: 'summary',
      key: 'summary',
      ellipsis: true,
      width: 200
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'
    },
    {
      title: 'Ngày phát hành',
      dataIndex: 'publishedAt',
      key: 'publishedAt',
      width: 140,
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={record.isPublished ? 'Hủy phát hành' : 'Phát hành'}>
            <Button
              type={record.isPublished ? 'default' : 'primary'}
              size="small"
              icon={<SendOutlined />}
              onClick={() => handlePublish(record.id, !record.isPublished)}
            />
          </Tooltip>
          <Tooltip title="Chỉnh sửa">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Xác nhận xóa thông báo này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Tooltip title="Xóa">
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <NotificationOutlined /> Quản lý Thông báo
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
          Tạo thông báo
        </Button>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Tổng số thông báo"
              value={stats?.total || 0}
              prefix={<NotificationOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Đã phát hành"
              value={stats?.published || 0}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Bản nháp"
              value={stats?.draft || 0}
              valueStyle={{ color: '#faad14' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Quan trọng"
              value={stats?.urgent || 0}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<NotificationOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="Loại thông báo"
            allowClear
            style={{ width: 150 }}
            onChange={(value) => setFilters(prev => ({ ...prev, type: value }))}
          >
            <Option value="general">Chung</Option>
            <Option value="urgent">Khẩn cấp</Option>
            <Option value="academic">Học thuật</Option>
            <Option value="financial">Tài chính</Option>
            <Option value="event">Sự kiện</Option>
          </Select>
          <Select
            placeholder="Đối tượng"
            allowClear
            style={{ width: 150 }}
            onChange={(value) => setFilters(prev => ({ ...prev, targetPortal: value }))}
          >
            <Option value="all">Tất cả</Option>
            <Option value="student">Sinh viên</Option>
            <Option value="teacher">Giảng viên</Option>
          </Select>
          <Select
            placeholder="Trạng thái"
            allowClear
            style={{ width: 150 }}
            onChange={(value) => setFilters(prev => ({ ...prev, isPublished: value }))}
          >
            <Option value="true">Đã phát hành</Option>
            <Option value="false">Bản nháp</Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => { loadNotifications(); loadStats(); }}>
            Làm mới
          </Button>
        </Space>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={notifications}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Tổng ${total} thông báo`,
            onChange: (page, pageSize) => setPagination(prev => ({ ...prev, page, limit: pageSize || 20 }))
          }}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ padding: '16px 0' }}>
                <Card size="small" title="Nội dung chi tiết">
                  <div dangerouslySetInnerHTML={{ __html: record.content.replace(/\n/g, '<br/>') }} />
                </Card>
              </div>
            )
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingNotification ? 'Chỉnh sửa thông báo' : 'Tạo thông báo mới'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="title"
                label="Tiêu đề"
                rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
              >
                <Input placeholder="Nhập tiêu đề thông báo" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="type" label="Loại thông báo">
                <Select>
                  <Option value="general">Chung</Option>
                  <Option value="urgent">Khẩn cấp</Option>
                  <Option value="academic">Học thuật</Option>
                  <Option value="financial">Tài chính</Option>
                  <Option value="event">Sự kiện</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="summary" label="Tóm tắt">
            <Input placeholder="Tóm tắt ngắn gọn (hiển thị trong danh sách)" />
          </Form.Item>

          <Form.Item
            name="content"
            label="Nội dung chi tiết"
            rules={[{ required: true, message: 'Vui lòng nhập nội dung' }]}
          >
            <TextArea rows={6} placeholder="Nhập nội dung chi tiết..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="targetPortal" label="Gửi đến">
                <Select>
                  <Option value="all">Tất cả</Option>
                  <Option value="student">Sinh viên</Option>
                  <Option value="teacher">Giảng viên</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="priority" label="Mức độ ưu tiên">
                <Select>
                  <Option value={0}>Bình thường</Option>
                  <Option value={1}>Quan trọng</Option>
                  <Option value={2}>Khẩn cấp</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="expiresAt" label="Hết hạn">
                <DatePicker 
                  showTime 
                  format="DD/MM/YYYY HH:mm"
                  placeholder="Chọn ngày hết hạn"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="imageUrl" label="Link ảnh">
                <Input placeholder="URL ảnh đính kèm" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="externalLink" label="Link bên ngoài">
                <Input placeholder="URL liên kết" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="isPublished" label="Phát hành ngay" valuePropName="checked">
            <Switch checkedChildren="Có" unCheckedChildren="Không" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editingNotification ? 'Cập nhật' : 'Tạo thông báo'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>Hủy</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NotificationManagementPage;


